/**
 * The NIQS Logbook Training and Mentoring Scheme terms, as approved by Council.
 *
 * Held as data rather than markup so the gate, and anything that later prints
 * or emails it, read from one copy. The training-code table is included even
 * though the same codes exist as a lookup the diary entry form queries — a
 * terms document is a fixed record of what was agreed, and must not change
 * shape because an admin edited a lookup row afterwards.
 *
 * TERMS_VERSION must match LogbookController::TERMS_VERSION on the API. When
 * Council revises the document, bump both together: the stored acceptance is
 * stamped with the version, and that is the whole point of keeping it.
 */
export const TERMS_VERSION = "4th-edition";

export type TermsClause = { ref?: string; text: string };
export type TermsSection = { title: string; intro?: string; clauses: TermsClause[] };

export const LOGBOOK_TERMS_INTRO: string[] = [
  "The logbook regime of The Nigerian Institute of Quantity Surveyors was introduced in 1989 by Late Chief Ezugo Isiadinso Council and was first reviewed in 2002. It was further reviewed in 2013 by Alufohai-led Council to produce the third edition. This third edition re-structured the logbook/diary and added in the documents necessary rules and guidelines to aid effective implementation of the programme. The essence was to bring a new vista in its use to achieve the much desired enhanced professional development of the young QS in order to attain maximum potentials expected of him/her. The current edition (fourth) incorporates recent updates in the operating guidelines as approved by the Council.",
  "The logbook programme is aimed at a systematic practical training of the young quantity surveyor to help him/her attain the desired level of competence required to render appropriate, standard, expected and acceptable professional services. It presents a coherent array of the various roles a QS is expected to play in the industry in terms of activities and their heads and sub-heads. It further puts in place a means of capturing in narrative the tutored experiences of the young QS in a bid to nurture him/her to maturity.",
];

/**
 * The sections, renumbered to run consecutively.
 *
 * The source is an extract, and its numbering shows it: Attestation is printed
 * as 1.4 with no 1.1–1.3 above it, the Diary notes as 2.x but AFTER the Log
 * Book, and the Log Book as 4 with no 3. Displayed verbatim that reads as a
 * document with pieces missing, and a candidate cannot tell whether a rule they
 * need is simply absent.
 *
 * So the sections are numbered 1–4 in the order they are shown. The Log Book
 * block keeps 4.1–4.18 untouched — those are the references candidates and
 * assessors actually cite ("4.16 report writing", "4.18 re-submission"), and
 * they happen to land on 4 anyway. Only the two short blocks move: Attestation
 * 1.4 → 2, Diary notes 2.x → 3.x.
 */
export const LOGBOOK_TERMS_SECTIONS: TermsSection[] = [
  {
    title: "2.  Attestation",
    clauses: [
      {
        ref: "2.1",
        text: "To be eligible to take the Graduateship Examination, candidates are required to have passed Stage One of the Diary and Log Book assessment.",
      },
      {
        ref: "2.2",
        text: "To be eligible to take the Test of Professional Competence (TPC) Examination, candidates will be required to pass the Second Stage of Diary and Log Book assessment.",
      },
      {
        ref: "2.3",
        text: "To be eligible to sit for Professional Competence Interview (PCI), candidates will be required to pass the final stage of Diary and Log Book assessment.",
      },
    ],
  },
  {
    title: "3.  Notes on the Diary",
    clauses: [
      {
        ref: "3.1",
        text: "The week(s) covered on each page of the Diary must be clearly indicated at the top left corner of the page.",
      },
      {
        ref: "3.2",
        text: "All entries in the Diary must indicate the corresponding Training Code for each activity obtainable from the analysis part of the Log Book.",
      },
      { ref: "3.3", text: "Time allocation to activities in the Diary must be in days with dates." },
      { ref: "3.4", text: "The description of the activities must be brief and straight forward." },
      {
        ref: "3.5",
        text: "The candidate and his/her supervisor must sign every page of the Diary as indicated.",
      },
      {
        ref: "3.6",
        text: "All completed Diaries must be returned to the Institute's Secretariat at the end of each stage of 12 months assessment.",
      },
    ],
  },
  {
    title: "4.  The Log Book",
    clauses: [
      {
        ref: "4.1",
        text: "The purpose of the Log Book is to enable candidates present analysis of their professional experience in a chronological order based on entries in their Diaries, which will assist the assessors to determine if their professional experience is adequate and properly balanced. It will also provide an easy reference to any particular aspect of the training as set out in the Diary.",
      },
      {
        ref: "4.2",
        text: "Logbooks are to be filled annually for three years in stages one to three and submitted at the end of each year (or on completion) for assessment. The current stage of assessment must be passed before the candidate proceeds to the next stage.",
      },
      {
        ref: "4.3",
        text: "Probationers can collect and start filling logbooks immediately after graduation (even during NYSC year if engaged in QS work).",
      },
      {
        ref: "4.4",
        text: "Issuers of logbook/diaries (on behalf of the National Secretariat and Chapters) should keep clear record of collection date of logbooks/diaries issued to probationers. This should be compiled by Chapter Chairmen at intervals and sent to the Institute's Secretariat.",
      },
      {
        ref: "4.5",
        text: "Logbooks/diaries collected are to be filled for one year starting from the date of collection. Logbooks and diaries are not to be filled in arrears unless in special cases where the candidate may be waiting for the result of previous submission before collecting the ones for the next stage.",
      },
      {
        ref: "4.6",
        text: "Assessment of logbooks/diaries is done twice in a year, in the months of February and July. January and June are designated months for submission of completed logbooks and diaries. Logbooks/diaries however, can still be submitted at other times. Closing dates for submission for each assessment will usually be announced before the assessment.",
      },
      {
        ref: "4.7",
        text: "Candidates are to select supervisors of their choice, secure their consent and submit their consent letters to the Secretariat not later than two (2) months after collection of the logbooks/diaries.",
      },
      {
        ref: "4.8",
        text: "Qualification for supervision: Supervisors of logbooks/diaries must be members with a minimum of 5 years post-MNIQS experience. The work of the supervisor is not merely to sign diaries and logbooks but to guide, mentor, coach and tutor the candidate through the process, and then endorse the entries during the work progress and at the end of the exercise.",
      },
      {
        ref: "4.9",
        text: "To help probationers identify qualified supervisors, a list of ACTIVE members with minimum of 5 years of post-MNIQS experience is published on the Institute's website.",
      },
      {
        ref: "4.10",
        text: "Training codes and coverage areas in diaries are not confined to QS in consultancy and contracting organizations. They also apply to QS in other organizations like manufacturing e.g. aluminium companies; oil and gas etc.",
      },
      {
        ref: "4.11",
        text: "The information entered in the analysis sheet must be extracted from the Diary at the end of each week. The days spent on each activity (stated in the days in the Diary) in each week must be summed up and entered into the appropriate space (cell) corresponding with the activity and the week in the analysis sheet.",
      },
      {
        ref: "4.12",
        text: "On completion of each stage of 12 months, the time (in days) spent on each activity as reflected in the Diary and transferred to the analysis sheet of the Log Book must be totaled both horizontally and vertically.",
      },
      {
        ref: "4.13",
        text: "While the vertical summary shows the total days spent on all activities per week, the horizontal summary shows the number of days spent on each activity throughout the duration of the practical experience.",
      },
      {
        ref: "4.14",
        text: "Log Books must be signed at least once every three (3) months by the candidate and his/her Supervisor.",
      },
      {
        ref: "4.15",
        text: "Each construction project undertaken or any other work carried out by the candidate or mentioned in the Diary shall be clearly described giving the project title, with brief description, location and at least name of the project team member with contract details (clients, consultants, contractor).",
      },
      {
        ref: "4.16",
        text: "Report Writing — At the end of each stage of twelve (12) months, the candidate is expected to write and submit a brief but articulate report of works undertaken in about three pages or 900 words. The report should have a clear introduction, summary of works executed, lessons learnt and a conclusion. An electronic copy of the report would be submitted along with a hard copy for purpose of defence.",
      },
      {
        ref: "4.17",
        text: "Oral Defence — At the end of each stage of the project work, the submissions by the candidates may, at the discretion of the education committee, be defended before a panel of assessors. The candidate is expected to explain the works undertaken and the experiences acquired.",
      },
      {
        ref: "4.18",
        text: "Re-Submission by unsuccessful candidates — A candidate who fails any stage of the Log Book assessment is expected to re-submit for the same stage in the next assessment period. This implies that a candidate who fails in February assessment could re-submit for same stage against July assessment; and he/she who fails in July assessment could re-submit against February assessment the following year.",
      },
    ],
  },
];

export type TrainingCodeRow = { code: string; description: string; heading?: boolean };

/** The training codes as they appear in the Log Book's analysis sheet. */
export const TRAINING_CODES: TrainingCodeRow[] = [
  { code: "TCD/A", description: "PRE-CONTRACT SERVICES", heading: true },
  { code: "TCD/A1", description: "Feasibility Studies" },
  { code: "TCD/A2", description: "Comparative Design Economics including cost plans and cost control" },
  { code: "TCD/A3", description: "Works Programme/Planning" },
  { code: "TCD/A4", description: "Preparation of Preliminary Estimates" },
  { code: "TCD/A5", description: "Measurement of Building Works/Preparation of Bill of Quantities" },
  { code: "TCD/A6", description: "Preparation of Schedule of Materials" },
  { code: "TCD/A7", description: "Preparation or Editing of Specifications" },
  { code: "TCD/A8", description: "Advice on Procurement Options" },
  { code: "TCD/A9", description: "Preparation of Unit Rates for Work Items/Pricing for Tendering Purposes" },
  { code: "TCD/A10", description: "Tender Evaluation/Report" },
  { code: "TCD/A11", description: "(Strategies for) Tender Negotiation" },

  { code: "TCD/B", description: "POST-CONTRACT SERVICES", heading: true },
  { code: "TCD/B1", description: "Administration of advance payments, retention, and performance bonds" },
  { code: "TCD/B2", description: "Measurement of works on site" },
  { code: "TCD/B3", description: "Application of Cost Control including Comparative Cost Analysis or Evaluation" },
  { code: "TCD/B4", description: "Preparation of Interim Valuations" },
  { code: "TCD/B5", description: "Preparation of Financial Statements" },
  { code: "TCD/B6", description: "Valuations of Variations/Fluctuations" },
  { code: "TCD/B7", description: "Preparation or Vetting of Loss and/or Expense or Insurance Claims" },
  { code: "TCD/B8", description: "Preparation and Agreement of Final Account" },
  { code: "TCD/B9", description: "Re-measurement of Provisional Quantities and Provisional Sum Items" },
  {
    code: "TCD/B10",
    description:
      "Analysis of Pricing for Regular Cost/Value Reconciliation Statements including Interpretation for Management",
  },
  { code: "TCD/B11", description: "Computation/Vetting of Fees" },
  { code: "TCD/B12", description: "Preparation of Cash Flow Forecast" },
  { code: "TCD/B13", description: "Resource Determination, Scheduling & Purchasing" },
  { code: "TCD/B14", description: "Contract Administration/Contractual Correspondence" },
  { code: "TCD/B15", description: "Production, Cost and Quality Control" },

  { code: "TCD/C", description: "SPECIALISATIONS", heading: true },
  { code: "TCD/C1", description: "Project Management" },
  { code: "TCD/C2", description: "Cost Benefit Analysis" },
  { code: "TCD/C3", description: "Insurance Consultancy" },
  { code: "TCD/C4", description: "Litigation and Arbitration" },
  { code: "TCD/C5", description: "Bankruptcy or Liquidation" },
  { code: "TCD/C6", description: "Information & Communication Technology (ICT)" },
  { code: "TCD/C7", description: "Research and Development" },
  { code: "TCD/C8", description: "Computation of Taxation Allowances" },
  { code: "TCD/C9", description: "Office Management" },
  { code: "TCD/C10", description: "Preparation of Schedule of Dilapidations/Condition Survey" },
  { code: "TCD/C11", description: "Civil Engineering Measurement" },
  { code: "TCD/C12", description: "Heavy Engineering Measurement" },
  { code: "TCD/C13", description: "M&E Installations Measurement" },
  { code: "TCD/C14", description: "ICT Installations Measurement" },

  { code: "TCD/D", description: "KNOWLEDGE OF THE INSTITUTE AND THE PROFESSION", heading: true },
  { code: "TCD/D1", description: "NIQS Constitution & Bye-laws" },
  { code: "TCD/D2", description: "Professional Ethics" },
  { code: "TCD/D3", description: "Professional Development e.g. Workshops & Seminars" },
  { code: "TCD/D4", description: "The Construction Industry" },
  { code: "TCD/D5", description: "Professional Practice/Engagement in Government or Industry" },

  { code: "TCD/E", description: "OTHER AREAS NOT COVERED ABOVE (LIST & SPECIFY)", heading: true },
  { code: "TCD/E1", description: "Financial Institutions (Banks, etc)" },
  { code: "TCD/E2", description: "Participation in Oil and Gas projects" },
  { code: "TCD/E3", description: "Heavy Engineering projects" },
  { code: "TCD/E4", description: "Manufacturing & Assembling Companies" },
  // E5–E8 are blank in the printed book: spare rows the candidate fills in.
  { code: "TCD/E5", description: "" },
  { code: "TCD/E6", description: "" },
  { code: "TCD/E7", description: "" },
  { code: "TCD/E8", description: "" },
];
